import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const inputSchema = z.object({
  name: z.string().trim().max(200).optional().or(z.literal("")),
  email: z.string().trim().max(320).optional().or(z.literal("")),
  brand_name: z.string().trim().min(1).max(200),
  url: z.string().trim().max(500).optional().or(z.literal("")),
  social_url: z.string().trim().max(500).optional().or(z.literal("")),
  warm_intros: z.string().max(500).optional().or(z.literal("")),
  referral_sources: z.string().max(1000).optional().or(z.literal("")),
  referral_activities: z.string().max(1000).optional().or(z.literal("")),
  referral_outreach: z.string().max(3000).optional().or(z.literal("")),
  self_description: z.string().max(3000).optional().or(z.literal("")),
  testimonial_status: z.string().max(500).optional().or(z.literal("")),
  growth_frustration: z.string().max(3000).optional().or(z.literal("")),
});

const gateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320),
});

async function fetchWebsiteText(url: string): Promise<string> {
  try {
    const normalized = url.startsWith("http") ? url : `https://${url}`;
    const res = await fetch(normalized, {
      headers: { "User-Agent": "Mozilla/5.0 AdvisorVisibilityScan/1.0" },
      signal: AbortSignal.timeout(10000),
    });
    if (!res.ok) return "";
    const html = await res.text();
    const cleaned = html
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<nav[\s\S]*?<\/nav>/gi, " ")
      .replace(/<footer[\s\S]*?<\/footer>/gi, " ")
      .replace(/<header[\s\S]*?<\/header>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned.slice(0, 3000);
  } catch {
    return "";
  }
}

export const analyzeScan = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => inputSchema.parse(data))
  .handler(async ({ data }) => {
    const apiKey = process.env.Claude_Scan_Key ?? process.env.ANTHROPIC_API_KEY;
    if (!apiKey) {
      throw new Error(
        "Claude_Scan_Key is not configured. Add it in Project Settings → Secrets.",
      );
    }

    const websiteText = data.url ? await fetchWebsiteText(data.url) : "";

    const userPrompt = `You are an expert advisor visibility strategist specializing in referral systems for RIA firms.

Analyze this firm using their website content AND their intake answers from a pre-call questionnaire. The intake answers are direct intelligence from the firm — weight them heavily when scoring.

Score each criterion from 1 to 5:
1 = Not present or very weak
2 = Minimal / underdeveloped
3 = Adequate but generic
4 = Clear and effective
5 = Exceptional / best-in-class

FIRM DETAILS
Firm Name: ${data.brand_name}
Website URL: ${data.url || "(not provided)"}
Social Media URL: ${data.social_url || "(not provided)"}

WEBSITE CONTENT
${websiteText || "(website content unavailable)"}

INTAKE ANSWERS (from pre-call questionnaire)
Warm introductions per month or quarter: ${data.warm_intros || "(not provided)"}
Current referral sources: ${data.referral_sources || "(not provided)"}
How they currently get in front of referral sources (activities): ${data.referral_activities || "(not provided)"}
Current outreach to referral sources: ${data.referral_outreach || "(not provided)"}
How they describe themselves when someone could refer them: ${data.self_description || "(not provided)"}
Client testimonials / experience reflections status: ${data.testimonial_status || "(not provided)"}
Biggest growth frustration: ${data.growth_frustration || "(not provided)"}

SCORING INSTRUCTIONS
- brand_clarity: Is the firm's purpose and niche immediately obvious from their website and how they describe themselves?
- positioning_distinctiveness: Do they stand apart from generic RIA competitors? Does their self-description match their website positioning?
- audience_specificity: Is the target client clearly defined on the website and in how they talk about their work?
- trust_signals: Testimonials, credentials, named results, social proof — present and specific?
- referral_readiness: Given their actual referral activity (intros per month, sources, outreach) and their self-description, how referral-ready are they in practice — not just on paper?
- cta_clarity: Are next steps clear and low-friction on the website?
- social_media_alignment: Does their social presence reinforce their positioning? Use intake context if website scrape is limited.
- visibility_gaps: Based on the gap between what they say on their website and what their intake answers reveal about actual referral activity, what are the most important things to fix?

Return ONLY valid JSON in exactly this format, no markdown, no extra text:
{ "brand_clarity": {"score": 0, "rationale": ""}, "positioning_distinctiveness": {"score": 0, "rationale": ""}, "audience_specificity": {"score": 0, "rationale": ""}, "trust_signals": {"score": 0, "rationale": ""}, "referral_readiness": {"score": 0, "rationale": ""}, "cta_clarity": {"score": 0, "rationale": ""}, "social_media_alignment": {"score": 0, "rationale": ""}, "visibility_gaps": {"observations": ""}, "immediate_actions": [{"priority": 1, "action": "", "why": ""}, {"priority": 2, "action": "", "why": ""}, {"priority": 3, "action": "", "why": ""}, {"priority": 4, "action": "", "why": ""}, {"priority": 5, "action": "", "why": ""}] }

For visibility_gaps: 3-5 sentences identifying the most specific gaps between what the firm projects publicly and what their intake answers reveal. Be direct and actionable.
For immediate_actions: 3-5 prioritized actions in order of highest impact, specific to this firm, not generic advice. The 'why' field explains why it matters for their referral visibility specifically.`;

    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5",
        max_tokens: 4000,
        system:
          "You are a brand strategist. Always respond with valid JSON only. No markdown, no code fences, no explanation — just the raw JSON object.",
        messages: [{ role: "user", content: userPrompt }],
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Anthropic API error (${res.status}): ${text.slice(0, 500)}`);
    }

    const payload = (await res.json()) as {
      content?: Array<{ type: string; text?: string }>;
    };
    const raw = payload.content?.find((c) => c.type === "text")?.text ?? "";
    const cleaned = raw
      .trim()
      .replace(/^```(?:json)?/i, "")
      .replace(/```$/i, "")
      .trim();

    let analysis: Record<string, any>;
    try {
      analysis = JSON.parse(cleaned);
    } catch {
      throw new Error("Could not parse analysis response. Please try again.");
    }

    await sendScanReport(data, analysis).catch((err) =>
      console.warn("scan report email failed", err),
    );

    let summaryEmailSentAt: string | null = null;
    if (data.email) {
      const sent = await sendScanSummary(data, analysis).catch((err) => {
        console.warn("scan summary email failed", err);
        return false;
      });
      if (sent) summaryEmailSentAt = new Date().toISOString();
    }

    await saveScanSubmission(
      data,
      analysis,
      websiteText.length > 0,
      summaryEmailSentAt,
    ).catch((err) => console.warn("scan submission save failed", err));

    return { analysis_json: cleaned, website_fetched: websiteText.length > 0 };
  });

type ScanInput = z.infer<typeof inputSchema>;

const RUBRIC_LABELS: Array<[string, string]> = [
  ["brand_clarity", "Brand Clarity"],
  ["positioning_distinctiveness", "Positioning Distinctiveness"],
  ["audience_specificity", "Audience Specificity"],
  ["trust_signals", "Trust Signals"],
  ["referral_readiness", "Referral Readiness"],
  ["cta_clarity", "Call-to-Action Clarity"],
  ["social_media_alignment", "Social Media Alignment"],
];

const INTAKE_LABELS: Array<[keyof ScanInput, string]> = [
  ["warm_intros", "Warm introductions per month or quarter"],
  ["referral_sources", "Current referral sources"],
  ["referral_activities", "How they get in front of referral sources"],
  ["referral_outreach", "Current outreach to referral sources"],
  ["self_description", "How they describe themselves"],
  ["testimonial_status", "Testimonials / experience reflections"],
  ["growth_frustration", "Biggest growth frustration"],
];

async function serverSupabase() {
  const { createClient } = await import("@supabase/supabase-js");
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

async function saveScanSubmission(
  data: ScanInput,
  analysis: Record<string, any>,
  websiteFetched: boolean,
  summaryEmailSentAt: string | null = null,
) {
  const supabase = await serverSupabase();
  await supabase.from("scan_submissions").insert({
    name: data.name || null,
    email: data.email || null,
    brand_name: data.brand_name,
    url: data.url || null,
    social_url: data.social_url || null,
    warm_intros: data.warm_intros || null,
    referral_sources: data.referral_sources || null,
    referral_activities: data.referral_activities || null,
    referral_outreach: data.referral_outreach || null,
    self_description: data.self_description || null,
    testimonial_status: data.testimonial_status || null,
    growth_frustration: data.growth_frustration || null,
    analysis,
    website_fetched: websiteFetched,
    summary_email_sent_at: summaryEmailSentAt,
  });
}

async function sendScanSummary(data: ScanInput, analysis: Record<string, any>) {
  const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
  const actions = Array.isArray(analysis["immediate_actions"])
    ? analysis["immediate_actions"].slice(0, 3)
    : [];
  const templateData = {
    name: data.name?.split(" ")[0] || "there",
    brand_name: data.brand_name,
    scores: RUBRIC_LABELS.map(([key, label]) => ({
      label,
      criterion: analysis[key] ?? {},
    })),
    top_actions: actions,
  };
  const stamp = Date.now();

  await sendTemplateEmail("scan-summary", data.email!, {
    templateData,
    idempotencyKey: `scan-summary-${data.email}-${stamp}`,
    from: "Katie Fernands <katie@katiefernands.com>",
    replyTo: "katie@katiefernands.com",
  });

  // Blind copy for Katie: the SDK has no bcc field, so send a second copy.
  void sendTemplateEmail("scan-summary", "kfernands1@gmail.com", {
    templateData,
    idempotencyKey: `scan-summary-copy-${data.email}-${stamp}`,
    from: "Katie Fernands <katie@katiefernands.com>",
    replyTo: data.email || undefined,
  }).catch((err) => console.warn("scan summary copy failed", err));

  return true;
}



async function sendScanReport(data: ScanInput, analysis: Record<string, any>) {
  const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
  await sendTemplateEmail("scan-report", "kfernands1@gmail.com", {
    templateData: {
      name: data.name || "",
      email: data.email || "",
      brand_name: data.brand_name,
      url: data.url || "",
      social_url: data.social_url || "",
      scores: RUBRIC_LABELS.map(([key, label]) => ({
        label,
        criterion: analysis[key] ?? {},
      })),
      visibility_gaps: analysis["visibility_gaps"]?.observations ?? "",
      immediate_actions: Array.isArray(analysis["immediate_actions"])
        ? analysis["immediate_actions"]
        : [],
      intake: INTAKE_LABELS.map(([key, label]) => ({
        label,
        value: (data[key] as string) || "",
      })),
    },
    idempotencyKey: `scan-report-${data.brand_name}-${Date.now()}`,
    replyTo: data.email || undefined,
  });
}

export const submitScanLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => gateSchema.parse(data))
  .handler(async ({ data }) => {
    const supabase = await serverSupabase();

    const { error } = await supabase.from("scan_leads").insert({
      name: data.name,
      email: data.email,
    });
    if (error) throw new Error("Something went wrong. Please try again.");

    void supabase
      .from("leads")
      .insert({
        name: data.name,
        email: data.email,
        source: "advisor-scan",
        message: "Started the Advisor Visibility Scan.",
      })
      .then(({ error: leadError }) => {
        if (leadError) console.warn("scan lead insert failed", leadError);
      });

    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    void sendTemplateEmail("lead-notification", "kfernands1@gmail.com", {
      templateData: {
        name: data.name,
        email: data.email,
        message: "Started the Advisor Visibility Scan.",
        source: "Advisor Visibility Scan",
      },
      idempotencyKey: `scan-lead-${data.email}-${Date.now()}`,
      replyTo: data.email,
    }).catch((err) => console.warn("scan lead notification failed", err));

    return { ok: true as const };
  });
